/**
 * @fileOverView
 * @description Login Page View<br>
 * @copyright 2015-present Flatirontek LLC
 **/

var LoginPageTemplate = require('../../templates/pages/loginpage.html');
var DocsPageTemplate = require('../../templates/pages/docspage.html');
var AmpersandView = require('ampersand-view');
var HeaderView = require('../widgets/header');
var LoginModel = require('../../models/login');
var app = require('ampersand-app');
var dialog = require('../../lib/dialog');

var LoginPage = AmpersandView.extend({

    template : LoginPageTemplate,

    initialize: function(options){

        const modelOptions = options.modelOptions || {};
        if(app.platform !== "browser" || app.serviceAuth) modelOptions.showStoreLinks = false;
        this.model = new LoginModel(modelOptions);

        if(window.localStorage.getItem('subscriptionKey')!=null) app.utils.retrieveSubscriptionAndDisplay(this.model);

        if(app.serviceAuth && options.serviceAuthModel) {
            this.serviceAuthModel = options.serviceAuthModel;
            this.listenTo(this.serviceAuthModel, 'change:configLoaded', this.updateServiceName);
            this.listenTo(this.serviceAuthModel, 'serviceNameLoaded', this.updateServiceName);
            
            if(this.serviceAuthModel.get('configLoaded')) {
                this.updateServiceName();
            }
        }
        
        if(app.serviceAuth) {
            this.updateServiceName();
        }

    },

    render: function(){
        const self = this;
        this._initializeSubviews();
        this.renderWithTemplate();
        if(app.pendingPasswordReset) this.openNewPasswordMode();
        if(this.query("input.vsdemo")!=null && app.platform=="browser"){
            app.utils.runTicker(self);
            app.tickerInterval = setInterval(function(){
                app.utils.runTicker(self);
            }, 45000);
        }
    },

    subviews: {
        mpheader: {
            selector : 'header',
            prepareView: function(el) {

                this.model.set('maintitle','Welcome to Vybit!');
                
                const clientId = app.queryParams['client_id'] || window.localStorage.getItem('serviceAuth');
                if(clientId){
                    const authType = window.localStorage.getItem('serviceAuthType');
                    if (authType === 'legacy') {
                        const displayName = app.config.serviceAuth[clientId]?.displayName || clientId;
                        this.model.set('maintitle','Vybit Authentication for ' + app.utils.shortAppName(displayName));
                    } else if (authType === 'oauth2') {
                        const serviceName = window.localStorage.getItem('oauth2ServiceName');
                        if (serviceName) {
                            this.model.set('maintitle','Vybit Authentication for ' + app.utils.shortAppName(serviceName));
                        } else {
                            this.model.set('maintitle','Vybit Authentication');
                        }
                    }
                }

                return new HeaderView({
                    el: el,
                    parent: this,
                    noback: true,
                    login: true,
                    maintitle: this.model.maintitle
                });
            }
        }
    },

    bindings: {
        "model.displayGettingStarted" : {
            type: "innerHTML",
            hook: "getting_started"
        },
        "model.displayStoreLinks" : {
            type: "innerHTML",
            hook: "storelinks"
        },
        "model.displayLoginMessage" : {
            type: "innerHTML",
            selector: "div.vloginmessage"
        },
        "model.displayLoginMessageClass" : {
            type: "class",
            selector: "div.vloginmessage"
        },
        "model.displayAppleLogin" : {
            type: "class",
            selector: "div.vappleloginbutton"
        },
        "model.passwordCardClass" : { type: "class", hook: "pwcard" },
        "model.passwordFieldVisibility" : { type: "class", hook: "pwpassword" },
        "model.passwordAltLinksVisibility" : { type: "class", hook: "pwaltlinks" },
        "model.passwordBackVisibility" : { type: "class", hook: "pwback" },
        "model.passwordSubmitLabel" : { type: "text", hook: "pwsubmit" },
        "model.passwordMessage" : { type: "innerHTML", hook: "pwmessage" },
        "model.passwordMessageClass" : { type: "class", hook: "pwmessage" }
    },

    switchPasswordMode: function(e){
        e.preventDefault();
        this.setPasswordMode(e.target.dataset.pwmode);
    },

    setPasswordMode: function(mode){
        this.model.set({passwordMode: mode, passwordMessage: '', passwordMessageClass: ''});
        const newPassword = mode === 'create' || mode === 'newpassword';
        //lets password managers offer a new password where one is being set and the saved one otherwise
        this.queryByHook('pwpassword').setAttribute('autocomplete', newPassword ? 'new-password' : 'current-password');
        this.queryByHook('pwemail').readOnly = mode === 'newpassword';
    },

    openNewPasswordMode: function(){
        this.setPasswordMode('newpassword');
        this.queryByHook('pwemail').value = app.pendingPasswordReset.email;
        this.queryByHook('pwpassword').focus();
    },

    setPasswordMessage: function(text, cssClass){
        this.model.set({passwordMessage: text, passwordMessageClass: cssClass || ''});
    },

    submitPassword: function(e){
        e.preventDefault();
        const mode = this.model.passwordMode;
        if(!mode){
            this.setPasswordMode('signin');
            this.queryByHook('pwemail').focus();
            return;
        }
        const email = this.queryByHook('pwemail').value.trim();
        const password = this.queryByHook('pwpassword').value;

        if(!email) return this.setPasswordMessage('Enter your email address.');
        if(mode === 'reset') return this.sendPasswordReset(email);
        if(!password) return this.setPasswordMessage('Enter your password.');
        if((mode === 'create' || mode === 'newpassword') && password.length < 8) return this.setPasswordMessage('Use a password of at least 8 characters.');

        if(mode === 'create') this.createPasswordAccount(email, password);
        else if(mode === 'newpassword') this.completePasswordReset(email, password);
        else this.signInWithPassword(email, password);
    },

    completePasswordReset: function(email, password){
        const self = this;
        app.utils.startSpinner();
        app.firebase.auth().confirmPasswordReset(app.pendingPasswordReset.code, password)
            .then(function(){
                app.pendingPasswordReset = null;
                self.signInWithPassword(email, password);
            })
            .catch(function(error){ self.failPassword(error); });
    },

    //onAuthStateChanged completes the sign in; unverified addresses are turned away there
    signInWithPassword: function(email, password){
        const self = this;
        if(!self.checkSignInNotInProgress()) return;
        self.setSignInProgress();
        app.utils.startSpinner();
        //the profile page shows which of the two "password" methods was used; set before the
        //sign in resolves, since onAuthStateChanged starts loading the profile first
        window.localStorage.setItem('signInMethod', 'password');

        app.firebase.auth().signInWithEmailAndPassword(email, password)
            .catch(function(error){
                window.localStorage.removeItem('signInMethod');
                self.clearSignInProgress();
                self.failPassword(error);
            });
    },

    //createUser signs the new user in; the unverified gate in onAuthStateChanged sends the email and signs them out
    createPasswordAccount: function(email, password){
        const self = this;
        app.utils.startSpinner();
        app.firebase.auth().createUserWithEmailAndPassword(email, password)
            .catch(function(error){ self.failPassword(error); });
    },

    sendPasswordReset: function(email){
        const self = this;
        const sent = 'If an account exists for <b>' + app.utils.escapeHtml(email) + '</b>, a password reset link is on its way.';
        app.firebase.auth().sendPasswordResetEmail(email, app.utils.emailActionSettings())
            .then(function(){ self.setPasswordMessage(sent, 'vpwinfo'); })
            .catch(function(error){
                //never confirm whether an address is registered
                if(error.code === 'auth/user-not-found') self.setPasswordMessage(sent, 'vpwinfo');
                else self.failPassword(error);
            });
    },

    failPassword: function(error){
        console.log('password sign-in error:', error);
        app.utils.stopSpinner();
        this.setPasswordMessage(this.passwordErrorMessage(error));
    },

    passwordErrorMessage: function(error){
        switch(error.code){
            case 'auth/invalid-email':
                return 'Enter a valid email address.';
            case 'auth/user-not-found':
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
            case 'auth/invalid-login-credentials':
                return 'Incorrect email or password.';
            case 'auth/too-many-requests':
                return 'Too many attempts. Wait a few minutes or reset your password.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists. Sign in with Google, Apple, or Email Link, or use "Forgot password?" to set a password for it.';
            case 'auth/weak-password':
                return 'Choose a stronger password.';
            case 'auth/expired-action-code':
            case 'auth/invalid-action-code':
                return 'That password reset link is invalid or has expired. Use "Forgot password?" to request a new one.';
            case 'auth/network-request-failed':
                return 'Network error. Try again.';
            default:
                return 'Something went wrong. Try again.';
        }
    },

    events: {
        "click #login-apple" : "signInApple",
        "click #login-google" : "signInGoogle",
        "click #login-email" : "signInEmail",
        "submit form[data-hook=pwpanel]" : "submitPassword",
        "click [data-pwmode]" : "switchPasswordMode",
        "click button.vsdemo" : "demoSoundSearch",
        "keypress input.vsdemo" : "dssOnEnter",
        "click span.vsdemoclear" : "clearSearchDemo"

    },

    signInGoogle: function () {
        var provider = new app.firebase.auth.GoogleAuthProvider();
        provider.setCustomParameters({prompt: "select_account"});
        this.signIn('google',provider);
    },

    signInApple: function () {
        var provider = new app.firebase.auth.OAuthProvider('apple.com');
        this.signIn('apple',provider);
    },

    signInEmail: async function(){
        const self = this;
        const email = await dialog.prompt('Send Authentication Link to this Email Address:', {title: 'Sign in with email link', type: 'email', placeholder: 'you@example.com', okText: 'Send link'});
        if(email === null) return;

        app.firebase.auth().sendSignInLinkToEmail(email, app.utils.emailActionSettings())
            .then(function() {
                // The link was successfully sent. Inform the user.
                // Save the email locally so you don't need to ask the user for it again
                // if they open the link on the same device.
                window.localStorage.setItem('emailForSignIn', email);
                document.querySelector('div.vloginmessage').innerHTML = "Sign In Link sent to<br/>" + app.utils.escapeHtml(email);

                //for email link inside ifttt app
                if(window.location.host=="app.vybit.net" && navigator.userAgent.match("(iPhone|iPad|Android).+Mobile")
                ){
                    dialog.prompt('If signing in from the IFTTT app, copy and paste the Email Link HERE and click OK:', {type: 'url'})
                        .then(function(emailLink){ self.processEmailLink(emailLink); });
                }

            })
            .catch(function(error) {
                console.log('sendSignInLinkToEmail error:', error);
                dialog.alert('Invalid Email Address. Try Again.');
            });
    },

    signIn: function(pname, provider){
        const self = this;

        if(!self.checkSignInNotInProgress()) return;

        self.setSignInProgress();
        document.querySelector('div.vloginmessage').innerHTML = "";
        app.utils.startSpinner();

        if((app.platformId === "android" || app.platformId === "ios") && pname === "google") {
            self.signInWithNativeGoogle();
        } else if(app.platformId === "ios" && pname === "apple") {
            self.signInWithNativeApple();
        } else {
            if(app.platformId === "ios") cordova.plugins.backgroundMode.enable();
            self.signInWithFirebaseRedirect(provider, pname);
        }
    },

    checkSignInNotInProgress: function() {
        const signInTimestamp = window.sessionStorage.getItem("signInInProgressTime");
        if(window.sessionStorage.getItem("signInInProgress") === "true") {
            const now = Date.now();
            const elapsed = now - parseInt(signInTimestamp || 0);

            if(elapsed < 60000) {
                return false;
            } else {
                this.clearSignInProgress();
            }
        }
        return true;
    },

    setSignInProgress: function() {
        window.sessionStorage.setItem("signInInProgress", "true");
        window.sessionStorage.setItem("signInInProgressTime", Date.now().toString());
    },

    clearSignInProgress: function() {
        window.sessionStorage.removeItem("signInInProgress");
        window.sessionStorage.removeItem("signInInProgressTime");
    },

    getProviderName: function(method) {
        const providers = {
            'google.com': 'Google',
            'apple.com': 'Apple',
            'facebook.com': 'Facebook',
            'password': 'Email'
        };
        return providers[method] || 'another provider';
    },

    handleAccountExistsError: function(error) {
        const self = this;
        if(error.code === 'auth/account-exists-with-different-credential') {
            const email = error.email;
            app.firebase.auth().fetchSignInMethodsForEmail(email).then(function(methods) {
                const providerName = self.getProviderName(methods[0]);
                if(methods[0] === 'facebook.com') {
                    dialog.alert('This email was registered with Facebook, which is no longer supported. Use "Sign in with Email Link" with this email address to access your account.');
                    return;
                }
                dialog.alert('This email is already registered with ' + providerName + '. Please sign in using ' + providerName + ' instead.');
            }).catch(function() {
                dialog.alert('This email is already registered with a different sign-in method. Please use the same method you used when you first created your account.');
            });
        } else {
            dialog.alert('Authentication failed: ' + error.message);
        }
    },

    signInWithNativeGoogle: function(){
        const self = this;

        if(typeof window.cordova === 'undefined' || typeof window.cordova.plugins === 'undefined' ||
           typeof window.cordova.plugins.GoogleSignInPlugin === 'undefined') {
            const provider = new app.firebase.auth.GoogleAuthProvider();
            provider.setCustomParameters({prompt: "select_account"});
            self.signInWithFirebaseRedirect(provider, 'google');
            return;
        }

        self.performGoogleSignIn();
    },

    performGoogleSignIn: function() {
        const self = this;

        window.cordova.plugins.GoogleSignInPlugin.signIn(
            function(response) {
                try {
                    let userData;
                    if(typeof response === 'string') {
                        const parsed = JSON.parse(response);
                        userData = parsed.message;
                    } else if(response.status === 'success') {
                        userData = response.message;
                    } else {
                        userData = response;
                    }

                    if(app.platformId === 'android') {
                        window.sessionStorage.removeItem("authInProgress");
                        window.sessionStorage.removeItem("signInInProgress");
                        window.sessionStorage.removeItem("signInInProgressTime");

                        app.auth.set('idToken', userData.id_token);
                        app.auth.set('provider', 'google.com');
                        app.auth.fetch({
                            success: function(model, response) {
                                model.set('authenticated', response.authenticated);
                                model.set('jwtToken', response.jwtToken);

                                window.localStorage.setItem('vybit_auth_idToken', userData.id_token);
                                window.localStorage.setItem('vybit_auth_jwtToken', response.jwtToken);
                                window.localStorage.setItem('vybit_auth_provider', 'google.com');
                                window.localStorage.setItem('vybit_auth_authenticated', 'true');

                                app.auth.checkAuth();
                                app.refreshFCMTokenRegistration();
                                app.utils.stopSpinner();
                            },
                            error: function(model, response) {
                                self.clearSignInProgress();
                                app.utils.stopSpinner();
                                dialog.alert('Authentication failed. Please try again.');
                            }
                        });
                    } else {
                        const credential = app.firebase.auth.GoogleAuthProvider.credential(userData.id_token);
                        app.firebase.auth().signInWithCredential(credential)
                            .catch(function(error) {
                                self.clearSignInProgress();
                                app.utils.stopSpinner();
                                self.handleAccountExistsError(error);
                            });
                    }
                } catch(parseError) {
                    self.clearSignInProgress();
                    app.utils.stopSpinner();
                    dialog.alert('Authentication parsing failed: ' + parseError.message);
                }
            },
            function(error) {
                let errorMessage = 'Google Sign-In failed. Please try again.';
                try {
                    let errorData;
                    if(typeof error === 'string') {
                        const parsed = JSON.parse(error);
                        errorData = parsed.message;
                    } else if(error.message) {
                        errorData = error.message;
                    } else {
                        errorData = error;
                    }

                    if(errorData && typeof errorData === 'string' && errorData.toLowerCase().includes('cancel')) {
                        errorMessage = null;
                    }
                } catch(parseError) {}

                self.clearSignInProgress();
                app.utils.stopSpinner();

                if(errorMessage) {
                    dialog.alert(errorMessage);
                }
            },
            {}
        );
    },

    signInWithNativeApple: function() {
        const self = this;

        if(typeof window.cordova === 'undefined' || typeof window.cordova.plugins === 'undefined' ||
           typeof window.cordova.plugins.SignInWithApple === 'undefined') {
            const provider = new app.firebase.auth.OAuthProvider('apple.com');
            self.signInWithFirebaseRedirect(provider, 'apple');
            return;
        }

        window.cordova.plugins.SignInWithApple.signin(
            { requestedScopes: [0, 1] },
            function(response) {
                try {
                    const identityToken = response.identityToken;
                    const nonce = response.nonce || null;

                    const provider = new app.firebase.auth.OAuthProvider('apple.com');
                    const credential = provider.credential({
                        idToken: identityToken,
                        rawNonce: nonce
                    });

                    app.firebase.auth().signInWithCredential(credential)
                        .catch(function(error) {
                            self.clearSignInProgress();
                            app.utils.stopSpinner();
                            self.handleAccountExistsError(error);
                        });
                } catch(parseError) {
                    self.clearSignInProgress();
                    app.utils.stopSpinner();
                    dialog.alert('Apple Sign-In parsing failed: ' + parseError.message);
                }
            },
            function(error) {
                self.clearSignInProgress();
                app.utils.stopSpinner();

                if(!(error && typeof error === 'object' && error.code === '1001')) {
                    dialog.alert('Apple Sign-In failed. Please try again.');
                }
            }
        );
    },

    signInWithFirebaseRedirect: function(provider, pname) {
        const self = this;

        app.firebase.auth().signInWithRedirect(provider)
            .catch(function (error) {
                self.clearSignInProgress();
                app.utils.stopSpinner();

                let failmsg = 'Authentication Failed. Try Again.';
                if(app.platformId === "ios") {
                    failmsg = "Authentication Failed.\nIn the device settings temporarily turn off\nSettings -> Safari -> Prevent Cross-Site Tracking\nand retry Sign In.";
                }
                dialog.alert(failmsg);
            });
    },


    processEmailLink: function(emailLink){
        if(!emailLink) return;
        var url = new URL(emailLink);
        var link = url.searchParams.get("link");
        window.location.href = link || emailLink;
    },

    demoSoundSearch: function(){ app.utils.demoSoundSearch(); },

    dssOnEnter: function(event){ app.utils.dssOnEnter(event); },

    clearSearchDemo: function(){
        window.localStorage.removeItem("demoSoundKey");
        window.localStorage.removeItem("demoSoundName");
        window.location.reload();
    },

    updateServiceName: function() {
        const serviceName = window.localStorage.getItem('oauth2ServiceName');
        if(!serviceName) return;
        
        const expectedTitle = `Vybit Authentication for ${app.utils.shortAppName(serviceName)}`;
        
        // Update header title via model
        if(this.subviews?.mpheader?.model?.get('maintitle') === 'Vybit Authentication') {
            this.subviews.mpheader.model.set('maintitle', expectedTitle);
        }
        
        // Fallback: Update header via DOM
        const headerEl = this.query('div.maintitle');
        if(headerEl?.textContent === 'Vybit Authentication') {
            headerEl.textContent = expectedTitle;
        }
        
        // Update login message
        const currentMessage = this.model.get('loginMessage');
        const clientId = app.queryParams.client_id || window.localStorage.getItem('serviceAuth');
        // the fallback message names the raw clientId until the service name loads
        const expectedMessage = this.model.serviceSignInMessage(serviceName);
        if(currentMessage && clientId && currentMessage !== expectedMessage && currentMessage.includes(clientId)) {
            this.model.set('loginMessage', expectedMessage);
        }
    },

    remove: function() {
        // Call parent remove
        AmpersandView.prototype.remove.call(this);
    }

});

module.exports = LoginPage;
